import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Trash2, Edit, Eye, Replace, Upload } from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_BASE_URL = 'https://backend.dlapakistan.com/api';
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

const DentallabJournal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image: null,
  });

  const fileInputRef = useRef(null);

  // Fetch cards from backend
  useEffect(() => {
    fetchCards();
  }, []);

  // FIXED: Added safety check to ensure response.data is an array
  const fetchCards = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dental-journal/cards/');
      // SAFETY CHECK: Ensure we always set an array
      const cardsData = Array.isArray(response.data) ? response.data : 
                        (response.data.results ? response.data.results : []);
      setCards(cardsData);
    } catch (err) {
      console.error('Fetch error:', err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load journal cards',
        icon: 'error',
        timer: 3000
      });
      // SAFETY CHECK: Set to empty array on error
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
        setFormData(prev => ({ ...prev, image: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit function - Backend integration
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = new FormData();
    data.append('title', formData.title);
    data.append('subtitle', formData.subtitle);
    
    if (formData.image) {
      data.append('image', formData.image);
    }
    
    try {
      if (isEditMode && currentCard) {
        // Update existing card
        await api.patch(`/dental-journal/cards/${currentCard.id}/`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        Swal.fire({
          title: 'Success',
          text: 'Card updated successfully!',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        // Create new card
        await api.post('/dental-journal/cards/', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        Swal.fire({
          title: 'Success',
          text: 'Card added successfully!',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
      
      // Refresh cards
      await fetchCards();
      handleCloseModal();
    } catch (err) {
      console.error('Error:', err.response?.data || err.message);
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.detail || err.response?.data?.message || 'Failed to save card',
        icon: 'error',
        timer: 3000
      });
    }
  };

  // Delete function
  const handleDeleteCard = async (cardId) => {
    const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (confirmed.isConfirmed) {
      try {
        await api.delete(`/dental-journal/cards/${cardId}/`);
        setCards(cards.filter(card => card.id !== cardId));
        Swal.fire({
          title: 'Deleted!',
          text: 'Card has been deleted.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (err) {
        Swal.fire({
          title: 'Error',
          text: 'Failed to delete card',
          icon: 'error',
          timer: 3000
        });
      }
    }
  };

  // Edit function
  const handleEditCard = (card) => {
    setCurrentCard(card);
    
    setFormData({
      title: card.title,
      subtitle: card.subtitle,
      image: null,
    });
    
    // Set image preview
    if (card.image_url) {
      setImagePreview(card.image_url);
    } else {
      setImagePreview(null);
    }
    
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  // View function
  const handleViewCard = (card) => {
    setCurrentCard(card);
    setIsViewModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentCard(null);
    setImagePreview(null);
    setFormData({ title: '', subtitle: '', image: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setCurrentCard(null);
  };

  // Trigger file input
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Remove image
  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Helper function to get image URL
  const getImageUrl = (card) => {
    if (card.image_url) {
      return card.image_url;
    }
    return '';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Empty state
  const renderEmptyState = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center">
        <Plus className="w-8 h-8 text-emerald-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">
        No journal cards yet
      </h3>
      <p className="text-gray-500 mb-6">
        Add your first journal card to get started
      </p>
      <button
        onClick={() => {
          setIsEditMode(false);
          setImagePreview(null);
          setIsModalOpen(true);
        }}
        className="px-6 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-md hover:shadow-lg"
      >
        Add Card
      </button>
    </div>
  );

  // Render cards grid - FIXED: Added safety check before mapping
  const renderGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8">
      {/* SAFETY CHECK: Only map if cards is an array */}
      {Array.isArray(cards) && cards.map((card) => (
        <div key={card.id} className="bg-white rounded-lg border border-gray-200 hover:border-emerald-300 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md group">
          <div className="relative h-80 overflow-hidden">
            <img 
              src={getImageUrl(card)}
              alt={card.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
              }}
            />
            <div className="absolute top-2 right-2 flex space-x-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCard(card);
                }}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-emerald-50 shadow-sm transition-all duration-200 hover:scale-105"
                title="Edit"
              >
                <Edit className="w-4 h-4 text-emerald-600" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCard(card.id);
                }}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-red-50 shadow-sm transition-all duration-200 hover:scale-105"
                title="Delete"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          </div>

          <div className="p-4 cursor-pointer" onClick={() => handleViewCard(card)}>
            <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">
              {card.title}
            </h3>
            <p className="text-gray-600 text-sm line-clamp-2 mb-2">
              {card.subtitle}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-emerald-600 font-medium">
                {formatDate(card.created_at)}
              </span>
              <Eye className="w-4 h-4 text-emerald-500" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex-1">
      {/* Header */}
      <div className="sticky top-14 z-10 bg-white px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dentallab Journal</h1>
            <p className="text-gray-600 mt-2">Manage journal articles and content cards</p>
          </div>
          
          <button
            onClick={() => {
              setIsEditMode(false);
              setImagePreview(null);
              setIsModalOpen(true);
            }}
            className="flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Card
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 pt-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading journal cards...</p>
          </div>
        ) : cards.length === 0 ? (
          renderEmptyState()
        ) : (
          renderGrid()
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-emerald-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                {isEditMode ? 'Edit' : 'Add'} Journal Card
              </h2>
              <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-6">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail Image *
                  </label>
                  {imagePreview ? (
                    <div className="border-2 border-dashed border-emerald-200 rounded-xl p-4">
                      <img src={imagePreview} alt="Preview" className="w-full h-48 object-contain rounded-lg mb-4" />
                      <div className="flex justify-center space-x-3">
                        <button type="button" onClick={triggerFileInput} className="flex items-center px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
                          <Replace className="w-4 h-4 mr-2" />
                          Replace
                        </button>
                        <button type="button" onClick={handleRemoveImage} className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div onClick={triggerFileInput} className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 transition-all duration-300">
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">Click to upload image</p>
                      <p className="text-xs text-gray-500 mt-2">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-300"
                    required
                    placeholder="Enter card title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.subtitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-300 h-32"
                    required
                    placeholder="Enter card description"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button 
                  type="button" 
                  onClick={handleCloseModal} 
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-5 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all duration-300"
                >
                  {isEditMode ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && currentCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-emerald-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Journal Card Details</h2>
              <button onClick={handleCloseViewModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <img src={getImageUrl(currentCard)} alt={currentCard.title} className="w-full h-64 object-cover rounded-lg mb-4" />
              <h3 className="text-xl font-bold mb-2">{currentCard.title}</h3>
              <p className="text-gray-700 mb-4">{currentCard.subtitle}</p>
              <div className="flex justify-between items-center text-sm text-gray-500">
                <span>Created: {formatDate(currentCard.created_at)}</span>
                <span>Updated: {formatDate(currentCard.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DentallabJournal;